import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Check, ArrowUpRight, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { ShareButtons } from "./ShareButtons";
import { TrustBadges } from "./TrustBadges";
import { trackProductClick } from "@/utils/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductCard = ({ product, index }) => {
  const [loading, setLoading] = useState(false);
  const featured = index % 2 === 0;

  const handleBuy = async () => {
    setLoading(true);
    trackProductClick(product.name, "kartu-beranda");
    try {
      const res = await axios.post(`${API}/products/${product.product_id}/click`, { source: "direct" });
      window.open(res.data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      window.open(product.affiliate_url, "_blank", "noopener,noreferrer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Reveal delay={index * 0.1} className={featured ? "" : "md:mt-24"}>
      <article
        data-testid={`product-card-${product.product_id}`}
        className="group border border-[#211B10]/10 bg-white hover:border-[#A67C1E]/40 transition-colors duration-500"
      >
        <div className="relative overflow-hidden aspect-[4/3]">
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <span className={`absolute top-6 left-6 font-mono text-[10px] uppercase tracking-[0.25em] backdrop-blur px-3 py-1.5 ${product.coming_soon ? "text-[#211B10] border border-[#211B10]/20 bg-[#FBF7EE]/90" : "text-[#EACB6B] border border-[#EACB6B]/40 bg-[#211B10]/85"}`}>
            {product.coming_soon ? "Segera Hadir" : "Pilihan Kurator"}
          </span>
        </div>

        <div className="p-8 md:p-10">
          <h3 className="font-display text-3xl md:text-4xl tracking-tight mb-3">{product.name}</h3>
          <p className="text-[#A67C1E] font-display italic text-lg mb-5">{product.tagline}</p>
          <p className="text-sm text-[#211B10]/60 leading-relaxed mb-8">{product.description}</p>

          {product.benefits?.length > 0 && (
            <ul className="space-y-3 mb-10">
              {product.benefits.slice(0, 5).map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-[#211B10]/75">
                  <Check className="w-4 h-4 text-[#A67C1E] mt-0.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          {product.coming_soon ? (
            <div data-testid={`coming-soon-note-${product.product_id}`} className="border border-[#A67C1E]/40 bg-[#A67C1E]/5 p-6">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A67C1E]">Open Pre-Order</p>
                <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#211B10]/55">Siap 2–4 minggu</span>
              </div>
              <p className="text-sm text-[#211B10]/70 leading-relaxed mb-4">
                Tersedia ukuran 10ml, 50ml, 100ml, 150ml — bahkan 250ml untuk ruangan luas. Harga menyusul, pemesan PO dapat prioritas.
              </p>
              <Link
                to={`/p/${product.slug || product.product_id}`}
                data-testid={`po-cta-${product.product_id}`}
                className="inline-flex items-center gap-2 bg-[#A67C1E] text-[#FBF7EE] font-medium text-xs uppercase tracking-[0.12em] px-6 py-3 hover:bg-[#211B10] transition-colors duration-300"
              >
                Pesan PO Sekarang <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                {product.price_display && (
                  <span data-testid={`product-price-${product.product_id}`} className="font-display text-2xl text-[#211B10]">
                    {product.price_display}
                  </span>
                )}
                <button
                  data-testid={`buy-btn-${product.product_id}`}
                  onClick={handleBuy}
                  disabled={loading}
                  className="inline-flex items-center gap-2 bg-[#A67C1E] text-[#FBF7EE] font-medium text-sm uppercase tracking-[0.12em] px-7 py-4 hover:bg-[#211B10] hover:-translate-y-1 transition-[background-color,transform] duration-300 disabled:opacity-60 disabled:translate-y-0"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
                  {product.cta_text || "Dapatkan Sekarang"}
                </button>
              </div>
              <div className="mt-5">
                <TrustBadges />
              </div>
            </>
          )}

          <div className="mt-8 pt-6 border-t border-[#211B10]/10 flex items-center justify-between gap-4 flex-wrap">
            <ShareButtons product={product} />
            <Link
              to={`/p/${product.slug || product.product_id}`}
              data-testid={`detail-link-${product.product_id}`}
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#211B10]/50 hover:text-[#A67C1E] transition-colors duration-300"
            >
              Lihat Detail <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </article>
    </Reveal>
  );
};

export const Products = ({ products, loading }) => (
  <section id="produk" className="py-24 md:py-32 bg-[#FBF7EE] relative">
    <div className="absolute top-1/3 -left-40 w-[30rem] h-[30rem] rounded-full bg-[#1B4332]/10 blur-[140px] pointer-events-none" aria-hidden="true" />
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#A67C1E] mb-6">Koleksi Pilihan</p>
        <h2 className="font-display tracking-tighter text-3xl md:text-5xl max-w-3xl leading-tight mb-6">
          Program yang mengubah cara Anda memandang uang.
        </h2>
        <p className="max-w-xl text-base text-[#211B10]/60 leading-relaxed mb-20">
          Klik tombol pada produk dan Anda akan diarahkan ke halaman checkout resmi. Prosesnya cepat, aman, dan akses langsung Anda terima.
        </p>
      </Reveal>

      {loading ? (
        <div data-testid="products-loading" className="flex items-center gap-3 text-[#211B10]/50 font-mono text-xs uppercase tracking-[0.2em]">
          <Loader2 className="w-4 h-4 animate-spin" /> Memuat koleksi...
        </div>
      ) : products.length === 0 ? (
        <p data-testid="products-empty" className="text-[#211B10]/50">Koleksi sedang disiapkan. Kembali lagi sebentar lagi.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-10 md:gap-16">
          {products.map((p, i) => (
            <ProductCard key={p.product_id} product={p} index={i} />
          ))}
        </div>
      )}
    </div>
  </section>
);
